import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import * as projectsApi from '../api/projects.api';
import type { Project, ProjectFormValues, ProjectList, ProjectUpdateValues } from '../types';
import { ProjectsContext } from './projectsContext';

const EMPTY_LIST: ProjectList = { items: [], enabled: false, limit: null, canCreate: false };

export const ProjectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAnonymous, isLoading: authLoading } = useAuth();
  const [list, setList] = useState<ProjectList>(EMPTY_LIST);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const refreshProjects = useCallback(async () => {
    if (!isAuthenticated || isAnonymous) {
      setList(EMPTY_LIST);
      setIsLoaded(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await projectsApi.listProjects();
      if (response.success && response.data) {
        setList(response.data);
      }
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
    }
  }, [isAuthenticated, isAnonymous]);

  useEffect(() => {
    if (authLoading) return;
    void refreshProjects();
  }, [authLoading, refreshProjects]);

  const createProject = useCallback(
    async (values: ProjectFormValues) => {
      const response = await projectsApi.createProject(values);
      if (response.success) await refreshProjects();
      return response;
    },
    [refreshProjects]
  );

  const updateProject = useCallback(async (projectId: string, values: ProjectUpdateValues) => {
    const response = await projectsApi.updateProject(projectId, values);
    if (response.success && response.data) {
      const updated: Project = response.data;
      setList((prev) => ({
        ...prev,
        items: prev.items.map((item) => (item.publicId === projectId ? updated : item)),
      }));
    }
    return response;
  }, []);

  const deleteProject = useCallback(
    async (projectId: string) => {
      const response = await projectsApi.deleteProject(projectId);
      if (response.success) await refreshProjects();
      return response;
    },
    [refreshProjects]
  );

  const value = useMemo(
    () => ({
      projects: list.items,
      isLoading,
      isLoaded,
      enabled: list.enabled,
      limit: list.limit,
      canCreate: list.canCreate,
      refreshProjects,
      createProject,
      updateProject,
      deleteProject,
    }),
    [createProject, deleteProject, isLoaded, isLoading, list, refreshProjects, updateProject]
  );

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
};
